// src/components/Profiles/UserProfile.js
import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import UploadPortal from './UploadPortal';
import SavedItems from './SavedItems';
import { UserContext } from '../../contexts/UserContext';
import '../../App.css';

const UserProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [userData, setUserData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [savedItems, setSavedItems] = useState([]);
  const [taggedItems, setTaggedItems] = useState([]);
  const [activeTab, setActiveTab] = useState('photos');
  const [showUploader, setShowUploader] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [userLikes, setUserLikes] = useState([]);
  const [userSaved, setUserSaved] = useState([]);
  const [hoveredItem, setHoveredItem] = useState(null);

  // Sample suggested accounts data
  const suggestedAccounts = [
    { id: 1, name: 'Gallery NY', username: 'gallery_ny', avatar: 'https://ui-avatars.com/api/?name=G&background=random' },
    { id: 2, name: 'Artist LA', username: 'artistla', avatar: 'https://ui-avatars.com/api/?name=A&background=random' },
    { id: 3, name: 'SF Institute', username: 'sf_institute', avatar: 'https://ui-avatars.com/api/?name=S&background=random' },
    { id: 4, name: 'Design Hub', username: 'design_hub', avatar: 'https://ui-avatars.com/api/?name=D&background=random' },
  ];

  const isOwnProfile = user?.email?.split('@')[0] === username;

  useEffect(() => {
    const fetchUserAndPosts = async () => {
      setIsLoading(true);
      try {
        // Fetch user data
        const currentUserEmail = user?.email?.split('@')[0];
        const isOwnProfile = currentUserEmail === username;
        
        let userDataResult = null;
        
        if (isOwnProfile && user?.uid) {
          const docRef = doc(db, 'users', user.uid);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const data = docSnap.data();
            userDataResult = { ...data, uid: user.uid };
          }
        }
        
        if (!userDataResult) {
          // Try username-based lookup or email lookup
          const docRef = doc(db, 'users', username.toLowerCase());
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            userDataResult = docSnap.data();
          } else {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('email', '==', `${username}@gmail.com`));
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
              const doc = querySnapshot.docs[0];
              userDataResult = { ...doc.data(), uid: doc.id };
            }
          }
        }

        if (userDataResult) {
          setUserData(userDataResult);
          
          // Filter legacy photos/videos arrays
          if (Array.isArray(userDataResult.photos)) {
            setPhotos(userDataResult.photos.filter(photo => photo && photo.trim() !== ''));
          }
          if (Array.isArray(userDataResult.videos)) {
            setVideos(userDataResult.videos.filter(video => video && video.trim() !== ''));
          }
          if (Array.isArray(userDataResult.savedItems)) {
            setSavedItems(userDataResult.savedItems.filter(item => item && item.trim() !== ''));
          }
          if (Array.isArray(userDataResult.taggedItems)) {
            setTaggedItems(userDataResult.taggedItems.filter(item => item && item.trim() !== ''));
          }

          // Fetch user's posts using JSON fallback for iOS performance
          try {
            // Skip Firebase - go straight to JSON fallback
            throw new Error('Using JSON fallback for faster iOS loading');
            
          } catch (postsError) {
            console.log('🔄 Loading posts from posts.json fallback...');
            
            try {
              const response = await fetch('/posts.json');
              const allPosts = await response.json();
              
              // Filter posts for this user
              const userPosts = allPosts.filter(post => 
                post.userId === (userDataResult.uid || username) || 
                post.username === username
              );
              
              console.log(`📋 Found ${userPosts.length} posts for user ${username}`);
              setPosts(userPosts);
              
            } catch (jsonError) {
              console.error('❌ Both Firebase and JSON failed:', jsonError);
              setPosts([]);
            }
          }
        }
        
        // Load current user's likes and saved items if logged in
        if (user?.uid) {
          const currentUserDoc = await getDoc(doc(db, 'users', user.uid));
          if (currentUserDoc.exists()) {
            const currentUserData = currentUserDoc.data();
            setUserLikes(currentUserData.likedItems || []);
            setUserSaved(currentUserData.savedItems || []);
          }
        }
        
      } catch (error) {
        console.error('Error fetching user and posts:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (username) {
      fetchUserAndPosts();
    }
  }, [username, user]);

  const formatDate = (date) => {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleLike = async (itemId) => {
    if (!user?.uid) return;

    const isLiked = userLikes.includes(itemId);
    
    try {
      const userRef = doc(db, 'users', user.uid);
      
      if (isLiked) {
        await updateDoc(userRef, {
          likedItems: arrayRemove(itemId)
        });
        setUserLikes(prev => prev.filter(id => id !== itemId));
      } else {
        await updateDoc(userRef, {
          likedItems: arrayUnion(itemId)
        });
        setUserLikes(prev => [...prev, itemId]);
      }
    } catch (error) {
      console.error('Error updating likes:', error);
    }
  };

  const handleSave = async (itemId) => {
    if (!user?.uid) return;

    const isSaved = userSaved.includes(itemId);
    
    try {
      const userRef = doc(db, 'users', user.uid);
      
      if (isSaved) {
        await updateDoc(userRef, {
          savedItems: arrayRemove(itemId)
        });
        setUserSaved(prev => prev.filter(id => id !== itemId));
      } else {
        await updateDoc(userRef, {
          savedItems: arrayUnion(itemId)
        });
        setUserSaved(prev => [...prev, itemId]);
      }
    } catch (error) {
      console.error('Error updating saved items:', error);
    }
  };

  const handleShare = async (itemId) => {
    const shareUrl = `${window.location.origin}/profiles/${username}/${itemId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Check out this content',
          url: shareUrl
        });
      } catch (error) {
        console.log('Share cancelled or failed');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  const renderTaggedUsers = (taggedUsers) => {
    if (!taggedUsers || taggedUsers.length === 0) return null;
    
    return (
      <div style={{ marginTop: '8px' }}>
        {taggedUsers.map((username, index) => (
          <Link
            key={index}
            to={`/profiles/${username}`}
            style={{
              color: '#0d47a1',
              textDecoration: 'none',
              marginRight: '8px',
              fontSize: '14px',
              fontWeight: 500
            }}
          >
            @{username}
          </Link>
        ))}
      </div>
    );
  };

  const renderKeywords = (keywords) => {
    if (!keywords || keywords.length === 0) return null;
    
    return (
      <div style={{ 
        marginTop: '12px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px'
      }}>
        {keywords.slice(0, 8).map((keyword, index) => (
          <span
            key={index}
            style={{
              background: '#f5f5f5',
              color: '#666',
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 500
            }}
          >
            {keyword}
          </span>
        ))}
        {keywords.length > 8 && (
          <span style={{ fontSize: '12px', color: '#999' }}>
            +{keywords.length - 8} more
          </span>
        )}
      </div>
    );
  };

  const toggleSelectItem = (url) => {
    setSelectedItems(prev =>
      prev.includes(url) ? prev.filter(item => item !== url) : [...prev, url]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) return;
    try {
      const docRef = doc(db, 'users', userData.uid || username.toLowerCase());
      let field, updatedArray;
      
      switch (activeTab) {
        case 'photos':
          field = 'photos';
          updatedArray = photos.filter(url => !selectedItems.includes(url));
          setPhotos(updatedArray);
          break;
        case 'videos':
          field = 'videos';
          updatedArray = videos.filter(url => !selectedItems.includes(url));
          setVideos(updatedArray);
          break;
        case 'saved':
          // Note: saved tab now uses SavedItems component, this case may not be used
          field = 'savedItems';
          updatedArray = savedItems.filter(url => !selectedItems.includes(url));
          setSavedItems(updatedArray);
          break;
        case 'tagged':
          field = 'taggedItems';
          updatedArray = taggedItems.filter(url => !selectedItems.includes(url));
          setTaggedItems(updatedArray);
          break;
        default:
          return;
      }
      
      await updateDoc(docRef, { [field]: updatedArray });
      setSelectedItems([]);
      setBulkDeleteMode(false);
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleFollowToggle = async () => {
    setIsFollowing(!isFollowing);
  };

  const handleEditProfile = () => {
    navigate('/edit-profile');
  };

  const handleUploadClick = () => {
    setShowUploader(prev => !prev);
  };

  const handleCloseUploader = () => {
    setShowUploader(false);
    // Refresh to show new posts
    window.location.reload();
  };

  const handleContentClick = (itemId) => {
    navigate(`/profiles/${username}/${itemId}`);
  };

  const renderInteractionOverlay = (item, itemId) => {
    const isLiked = userLikes.includes(itemId);
    const isSaved = userSaved.includes(itemId);

    return (
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.8) 100%)',
        opacity: hoveredItem === itemId ? 1 : 0,
        transition: 'opacity 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        padding: '20px',
        borderRadius: '12px'
      }}>
        {/* Tagged users on hover */}
        {item.taggedUsers && item.taggedUsers.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            {item.taggedUsers.map((taggedUser, index) => (
              <Link
                key={index}
                to={`/profiles/${taggedUser}`}
                style={{
                  color: 'white',
                  textDecoration: 'none',
                  marginRight: '8px',
                  fontSize: '13px',
                  fontWeight: 500,
                  textShadow: '0 1px 2px rgba(0,0,0,0.8)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                @{taggedUser}
              </Link>
            ))}
          </div>
        )}
        
        {/* Interaction buttons */}
        {user && !user.isGuest && (
          <div style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center'
          }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleLike(itemId);
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '13.6px',
                color: isLiked ? '#e91e63' : 'white',
                textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                transition: 'color 0.2s ease'
              }}
            >
              {isLiked ? '♥' : '♡'}
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleSave(itemId);
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12.24px',
                color: isSaved ? '#0d47a1' : 'white',
                textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                transition: 'color 0.2s ease'
              }}
            >
              {isSaved ? '★' : '☆'}
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleShare(itemId);
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                color: 'white',
                textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                transition: 'color 0.2s ease'
              }}
            >
              ↗
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderLegacyContent = () => {
    // Handle saved tab with SavedItems component
    if (activeTab === 'saved') {
      return <SavedItems username={username} />;
    }

    let items = [];
    let isPhotosWithPosts = false;
    
    switch (activeTab) {
      case 'photos':
        items = photos;
        isPhotosWithPosts = true;
        break;
      case 'videos':
        items = videos;
        break;
      case 'tagged':
        items = taggedItems;
        break;
      case 'products':
        return (
          <div className="discover-grid">
            <p>No products to display yet.</p>
          </div>
        );
      default:
        items = photos;
        isPhotosWithPosts = true;
    }

    // For Photos tab, show rich posts first, then legacy photos
    if (isPhotosWithPosts && posts.length > 0) {
      return (
        <div>
          {/* Rich Posts */}
          <div style={{ 
            maxWidth: '600px', 
            margin: '0 auto 27.2px',
            padding: '0 20px'
          }}>
            {posts.map((post) => {
              const isLiked = userLikes.includes(post.id);
              const isSaved = userSaved.includes(post.id);
              
              return (
                <article 
                  key={post.id}
                  style={{
                    marginBottom: '60px',
                    background: 'white',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                    border: '1px solid #f0f0f0',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleContentClick(post.id)}
                >
                  {/* Post Header */}
                  <div style={{ 
                    padding: '20px 24px 16px',
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Link 
                          to={`/profiles/${post.username || username}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: '#e5e5e5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#666'
                          }}>
                            {(post.username || username).charAt(0).toUpperCase()}
                          </div>
                        </Link>
                        <div>
                          <Link 
                            to={`/profiles/${post.username || username}`}
                            style={{ 
                              textDecoration: 'none',
                              color: '#333',
                              fontWeight: 600,
                              fontSize: '14px'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {post.username || username}
                          </Link>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#999',
                            marginTop: '2px'
                          }}>
                            {formatDate(post.createdAt)}
                          </div>
                        </div>
                      </div>
                      
                      {/* Project Type Badge */}
                      {post.projectType && (
                        <span style={{
                          background: '#f0f9ff',
                          color: '#0369a1',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {post.projectType.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Post Image */}
                  <div style={{ position: 'relative' }}>
                    <img
                      src={post.imageUrl}
                      alt={post.description}
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '408px',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>

                  {/* Post Content */}
                  <div style={{ padding: '24px' }}>
                    {/* Actions */}
                    {user && !user.isGuest && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '20px',
                        marginBottom: '20px'
                      }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLike(post.id);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '24px',
                            color: isLiked ? '#e91e63' : '#666',
                            transition: 'color 0.2s ease'
                          }}
                        >
                          {isLiked ? '♥' : '♡'}
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSave(post.id);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '13.6px',
                            color: isSaved ? '#0d47a1' : '#666',
                            transition: 'color 0.2s ease'
                          }}
                        >
                          {isSaved ? '★' : '☆'}
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShare(post.id);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '12.24px',
                            color: '#666',
                            transition: 'color 0.2s ease'
                          }}
                        >
                          ↗
                        </button>
                      </div>
                    )}

                    {/* Description */}
                    <div style={{
                      fontSize: '10.2px',
                      lineHeight: '1.6',
                      color: '#333',
                      marginBottom: '16px',
                      fontFamily: 'Georgia, serif'
                    }}>
                      {post.description}
                    </div>

                    {/* Location */}
                    {post.location && (
                      <div style={{
                        fontSize: '8.84px',
                        color: '#666',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        📍 {post.location}
                      </div>
                    )}

                    {/* Tagged Users */}
                    {renderTaggedUsers(post.taggedUsers)}

                    {/* Keywords */}
                    {renderKeywords(post.keywords)}

                    {/* Post Stats */}
                    <div style={{
                      marginTop: '20px',
                      paddingTop: '16px',
                      borderTop: '1px solid #f0f0f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        gap: '16px',
                        fontSize: '8.84px',
                        color: '#666'
                      }}>
                        <span>{post.likes || 0} likes</span>
                        <span>{post.views || 0} views</span>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {/* Legacy Photos (if any) */}
          {items.length > 0 && (
            <div>
              <div style={{ 
                textAlign: 'center', 
                margin: '27.2px 0 13.6px',
                color: '#666',
                fontSize: '14px'
              }}>
                Legacy Photos
              </div>
              <div className="discover-grid">
                {items.map((item, index) => {
                  const itemId = `legacy-photo-${index}`;
                  
                  return (
                    <div 
                      key={itemId} 
                      className="discover-card"
                      style={{
                        position: 'relative',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleContentClick(itemId)}
                      onMouseEnter={() => setHoveredItem(itemId)}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      {bulkDeleteMode && (
                        <input
                          type="checkbox"
                          className="bulk-checkbox"
                          checked={selectedItems.includes(item)}
                          onChange={() => toggleSelectItem(item)}
                        />
                      )}
                      <img 
                        src={item} 
                        alt={`Legacy photo ${index}`} 
                        className="card-image"
                        style={{
                          width: '100%',
                          height: '476px',
                          objectFit: 'cover',
                          borderRadius: '12px'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      
                      {/* Hover overlay for legacy content */}
                      {renderInteractionOverlay({ taggedUsers: [] }, itemId)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    }

    // For other tabs or Photos with no posts, show legacy content
    if (activeTab === 'photos' && posts.length === 0 && items.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '27.2px', color: '#666' }}>
          <p>No photos shared yet.</p>
        </div>
      );
    }

    if (items.length === 0) {
      return <p>No {activeTab} to display yet.</p>;
    }

    return (
      <div className="discover-grid">
        {items.map((item, index) => {
          const itemId = `${activeTab}-${index}`;
          
          return (
            <div 
              key={index} 
              className="discover-card"
              style={{
                position: 'relative',
                cursor: 'pointer'
              }}
              onClick={() => handleContentClick(itemId)}
              onMouseEnter={() => setHoveredItem(itemId)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              {bulkDeleteMode && (
                <input
                  type="checkbox"
                  className="bulk-checkbox"
                  checked={selectedItems.includes(item)}
                  onChange={() => toggleSelectItem(item)}
                />
              )}
              {activeTab === 'videos' ? (
                <video
                  src={item}
                  className="card-image"
                  style={{
                    width: '100%',
                    height: '476px',
                    objectFit: 'cover',
                    borderRadius: '12px'
                  }}
                  muted
                  loop
                  playsInline
                  autoPlay
                  onMouseOver={e => e.target.play()}
                  onMouseOut={e => e.target.pause()}
                  controls={false}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <img 
                  src={item} 
                  alt={`User ${activeTab} ${index}`} 
                  className="card-image"
                  style={{
                    width: '100%',
                    height: '476px',
                    objectFit: 'cover',
                    borderRadius: '12px'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              
              {/* Hover overlay for legacy content */}
              {renderInteractionOverlay({ taggedUsers: [] }, itemId)}
            </div>
          );
        })}
      </div>
    );
  };

  if (isLoading) return <main className="profile-page">Loading user profile...</main>;
  if (!userData) return <main className="profile-page">User not found.</main>;

  return (
    <>
      <div className="discover-container">
        <div className="discover-main">
          <section className="profile-banner">
            <div className="digital-card-container">
              <h1>{userData.displayName?.toUpperCase() || username.toUpperCase()}</h1>
              
              {userData.discipline && (
                <div style={{ 
                  marginTop: '0.5rem',
                  fontSize: '0.612rem',
                  color: '#666',
                  fontWeight: 500
                }}>
                  {userData.discipline}
                </div>
              )}
              
              <p className="profile-description">{userData.bio || 'No bio yet.'}</p>
              
              <div style={{
                display: 'flex',
                gap: '2rem',
                marginTop: '1rem',
                fontSize: '0.9rem',
               color: '#666'
             }}>
               <span>
                 <strong style={{color: '#000'}}>{posts.length + (photos.length + videos.length)}</strong> posts
               </span>
               <span>
                 <strong style={{color: '#000'}}>{userData.followersCount || 0}</strong> followers
               </span>
               <span>
                 <strong style={{color: '#000'}}>{userData.followingCount || 0}</strong> following
               </span>
             </div>
             
             {userData.website && (
               <div style={{ marginTop: '0.5rem' }}>
                 <a 
                   href={userData.website.startsWith('http') ? userData.website : `https://${userData.website}`}
                   target="_blank"
                   rel="noopener noreferrer"
                   style={{
                     color: '#0d47a1',
                    textDecoration: 'none',
                    fontSize: '0.612rem'
                  }}
                >
                  {userData.website}
                </a>
              </div>
            )}
          </div>
          
          <div className="profile-controls" style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            alignItems: 'center'
          }}>
            {isOwnProfile ? (
              <>
                {/* Contributor Dashboard Button - Only for approved contributors */}
                {userData.approvedForUploads && (
                  <Link 
                    to="/contributor-dashboard" 
                    className="control-button contributor-dashboard-btn"
                    style={{
                      background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                      color: 'white',
                      fontWeight: '600',
                      border: '2px solid transparent',
                      boxShadow: '0 2px 8px rgba(39, 174, 96, 0.2)'
                    }}
                  >
                    🤝 Contributor Dashboard
                  </Link>
                )}
                
                <button className="control-button" onClick={handleEditProfile}>Edit Profile</button>
                <button className="control-button">Share Profile</button>
                <button 
                  className="control-button" 
                  onClick={handleUploadClick}
                >
                  {showUploader ? 'Close Uploader' : 'Upload Content'}
                </button>
                <Link to="/card-generator" className="control-button">
                  View Digital Card
                </Link>
                <Link to="/user-settings" className="control-button">
                  User Settings
                </Link>
              </>
            ) : (
              <>
                <button 
                  className="control-button"
                  onClick={handleFollowToggle}
                  style={{
                    backgroundColor: isFollowing ? '#f5f5f5' : '#0d47a1',
                    color: isFollowing ? '#000' : '#fff'
                  }}
                >
                  {isFollowing ? 'Following' : 'Follow'}
                </button>
                <button className="control-button">Message</button>
              </>
            )}
            
            {!bulkDeleteMode && isOwnProfile && (
              <div className="dropdown-container" onMouseLeave={() => setDropdownOpen(false)}>
                <button className="dropdown-toggle" onClick={() => setDropdownOpen(prev => !prev)}>
                  …
                </button>
                {dropdownOpen && (
                  <div className="dropdown-menu">
                    <div className="dropdown-item" onClick={() => { setBulkDeleteMode(true); setDropdownOpen(false); }}>
                      Delete Content
                    </div>
                  </div>
                )}
              </div>
            )}
            {bulkDeleteMode && (
              <button 
                className="control-button" 
                onClick={() => { setBulkDeleteMode(false); setSelectedItems([]); }}
              >
                Done
              </button>
            )}
          </div>
        </section>

        {showUploader && <UploadPortal onClose={handleCloseUploader} />}

        <section className="profile-tabs">
          <button onClick={() => setActiveTab('photos')} className={`tab-button ${activeTab === 'photos' ? 'active' : ''}`}>Photos</button>
          <button onClick={() => setActiveTab('videos')} className={`tab-button ${activeTab === 'videos' ? 'active' : ''}`}>Videos</button>
          {isOwnProfile && (
            <button onClick={() => setActiveTab('saved')} className={`tab-button ${activeTab === 'saved' ? 'active' : ''}`}>Saved</button>
          )}
          <button onClick={() => setActiveTab('tagged')} className={`tab-button ${activeTab === 'tagged' ? 'active' : ''}`}>Tagged</button>
          <button onClick={() => setActiveTab('products')} className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}>Products</button>
        </section>

        <section className="profile-feed">
          {bulkDeleteMode && (
            <button onClick={handleDeleteSelected} className="action-button" style={{ marginBottom: '1rem' }}>
              Delete Selected
            </button>
          )}

          {renderLegacyContent()}
        </section>
      </div>

      {/* Sidebar */}
      <div className="discover-sidebar">
        <div style={{
          padding: '1rem',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          marginBottom: '1.5rem'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h3 style={{margin: 0, fontSize: '1rem', fontWeight: 600}}>Suggested Profiles</h3>
            <Link to="/profiles" style={{
              textDecoration: 'none',
              color: '#0d47a1',
              fontSize: '0.8rem',
              fontWeight: 500
            }}>
              See All
            </Link>
          </div>
          
          <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
            {suggestedAccounts.map(account => (
              <div 
                key={account.id} 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}
              >
                <Link to={`/profiles/${account.username}`} style={{
                  flexShrink: 0,
                  width: '27.2px',
                  height: '27.2px',
                  borderRadius: '50%',
                  overflow: 'hidden'
                }}>
                  <img 
                    src={account.avatar} 
                    alt={account.name} 
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Link>
                <div style={{flexGrow: 1, overflow: 'hidden'}}>
                  <Link to={`/profiles/${account.username}`} style={{
                    display: 'block',
                    textDecoration: 'none',
                    color: '#111',
                    fontWeight: 600,
                    fontSize: '0.612rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {account.name}
                  </Link>
                  <span style={{
                    display: 'block',
                    fontSize: '0.8rem',
                    color: '#666',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    @{account.username}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div style={{padding: '0.5rem 0.25rem'}}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.75rem',
            marginBottom: '0.75rem',
            fontSize: '0.75rem',
            color: '#888'
          }}>
            <Link to="/about" style={{cursor: 'pointer', color: '#888', textDecoration: 'none'}}>About</Link>
            <Link to="/help" style={{cursor: 'pointer', color: '#888', textDecoration: 'none'}}>Help</Link>
            <Link to="/terms" style={{cursor: 'pointer', color: '#888', textDecoration: 'none'}}>Terms</Link>
            <Link to="/privacy" style={{cursor: 'pointer', color: '#888', textDecoration: 'none'}}>Privacy</Link>
          </div>
          <div style={{fontSize: '0.75rem', color: '#888'}}>
            © Artifex {new Date().getFullYear()}
          </div>
        </div>
      </div>
    </div>
  </>
);
};

export default UserProfile;